/**
 * Parsing utilities for email detection and domain extraction.
 */

import psl from 'psl';

/**
 * Email validation regex pattern.
 * Matches standard email formats like user@domain.tld
 */
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * Check if a string is a valid email address.
 *
 * @param text - The string to validate
 * @returns true if the string is a valid email address, false otherwise
 */
export function isEmail(text: string): boolean {
  return EMAIL_REGEX.test(text);
}

/**
 * Extract the root domain from a URL or email address.
 *
 * Uses the psl (Public Suffix List) library to properly extract the root domain,
 * handling cases like subdomains (www.acme.com -> acme.com) and country-code
 * TLDs (example.co.uk -> example.co.uk).
 *
 * @param text - A URL (with or without protocol) or email address
 * @returns The root domain, or null if extraction fails
 */
export function extractDomain(text: string): string | null {
  if (!text || text.trim() === '') {
    return null;
  }

  const trimmed = text.trim();
  let hostname: string;

  // Check if it contains @ (potential email)
  if (trimmed.includes('@')) {
    // If it looks like an email attempt, only process if it's valid
    if (isEmail(trimmed)) {
      const parts = trimmed.split('@');
      hostname = parts[1];
    } else {
      // Invalid email format - return null
      return null;
    }
  } else {
    // Try to parse as URL
    try {
      // Add protocol if missing to make URL parsing work
      const urlString = trimmed.match(/^https?:\/\//i)
        ? trimmed
        : `https://${trimmed}`;
      const url = new URL(urlString);
      ({ hostname } = url);
    } catch {
      return null;
    }
  }

  // Use psl to extract root domain
  const parsed = psl.parse(hostname);
  if (parsed.error || !parsed.domain) {
    // psl failed to parse - not a valid domain
    return null;
  }

  return parsed.domain;
}
